//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import * as twitterAnalyticsApi from '@apis/twitter-analytics/twitterAnalyticsApi';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import {
  IGetEngagementMetricsResponse,
  IMaterializedTweetWithMetrics,
  ITwitterDailyEngagementMetric,
  TwitterMetricSortKey,
} from '@apis/twitter-analytics/twitterAnalyticsApiModel';
import {
  getAliasedDateRange,
  IAliasedDateRange,
} from '@components/date-range-picker/dateRangePickerModels';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { createThunk } from '@redux/createThunk';
import { SliceNames } from '@redux/SliceNames';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ITwitterEngagementPageState,
  TwitterTopTweetView,
} from '@twitterV2/redux/engagement/twitterEngagementModel';
import { fillInMissingEngagementDates } from '@twitterV2/twitterUtils';
import { isDefined } from '@utils/filterPredicates';
import { crl8 } from 'app/curalate.globals';

// TODO: Remove dummy data after APIs are done
const initialState: ITwitterEngagementPageState = {
  dateRange: getAliasedDateRange(
    'last7Days',
    crl8.session?.timeZone.id ?? TimeZoneId.Utc,
  ),
  dailyStats: [],
  summaryStats: undefined,
  topTweets: {
    sortKey: TwitterMetricSortKey.PostedTimestamp,
    view: TwitterTopTweetView.TableView,
    offset: 0,
    limit: 8,
    total: 0,
    tweets: [],
  },
  flags: {
    isFetchingDailyStats: false,
    isFetchingSummaryStats: false,
    isFetchingTopTweets: false,
  },
  errors: {
    fetchDailyStatsError: undefined,
    fetchSummaryStatsError: undefined,
    fetchTopTweetsError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.TwitterEngagement,
  initialState,
  reducers: {
    updateDateRange(state, action: PayloadAction<IAliasedDateRange>) {
      state.dateRange = action.payload;
    },
    updateTopTweetsDisplayState(
      state,
      action: PayloadAction<{
        sortKey?: TwitterMetricSortKey;
        view?: TwitterTopTweetView;
        offset?: number;
        limit?: number;
      }>,
    ) {
      state.topTweets.sortKey =
        action.payload.sortKey ?? state.topTweets.sortKey;
      state.topTweets.view = action.payload.view ?? state.topTweets.view;
      state.topTweets.offset = action.payload.offset ?? state.topTweets.offset;
      state.topTweets.limit = action.payload.limit ?? state.topTweets.limit;
    },
    requestDailyStats(state) {
      state.flags.isFetchingDailyStats = true;
      state.errors.fetchDailyStatsError = undefined;
    },
    receiveDailyStatsSucceeded(
      state,
      action: PayloadAction<ITwitterDailyEngagementMetric[]>,
    ) {
      state.flags.isFetchingDailyStats = false;
      state.dailyStats = { ...action.payload };
    },
    receiveDailyStatsFailed(state, action: PayloadAction<string | undefined>) {
      state.flags.isFetchingDailyStats = false;
      state.errors.fetchDailyStatsError = action.payload;
    },
    requestSummaryStats(state) {
      state.flags.isFetchingSummaryStats = true;
      state.errors.fetchSummaryStatsError = undefined;
    },
    receiveSummaryStatsSucceeded(
      state,
      action: PayloadAction<IGetEngagementMetricsResponse>,
    ) {
      state.flags.isFetchingSummaryStats = false;
      state.summaryStats = { ...action.payload.data.summaryMetrics };
      state.dailyStats = [
        ...fillInMissingEngagementDates(
          action.payload.data.dailyMetrics,
          state.dateRange.startDate,
          state.dateRange.endDate,
        ),
      ];
    },
    receiveSummaryStatsFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingSummaryStats = false;
      state.errors.fetchSummaryStatsError = action.payload;
    },
    requestUserTweets(state) {
      state.flags.isFetchingTopTweets = true;
      state.errors.fetchTopTweetsError = undefined;
    },
    receiveUserTweetsSucceeded(
      state,
      action: PayloadAction<{
        tweets: IMaterializedTweetWithMetrics[];
        total: number;
      }>,
    ) {
      state.flags.isFetchingTopTweets = false;
      state.topTweets.tweets = [...action.payload.tweets];
      state.topTweets.total = action.payload.total;
    },
    receiveUserTweetsFailed(state, action: PayloadAction<string | undefined>) {
      state.flags.isFetchingTopTweets = false;
      state.errors.fetchTopTweetsError = action.payload;
    },
  },
});

export const changeDateRangeAndRefreshContent = createThunk(
  (dateRange: IAliasedDateRange) => (dispatch) => {
    dispatch(actions.updateDateRange(dateRange));

    void dispatch(fetchEngagementMetrics(dateRange));
    void dispatch(fetchUserTweets(dateRange));
  },
);

export const updateTopTweetsFiltersAndFetchContent = createThunk(
  (sortKey: TwitterMetricSortKey) => (dispatch, getState) => {
    const { dateRange } = getState().twitter.engagement;
    dispatch(
      updateTopTweetsDisplayState({
        sortKey,
        offset: 0, // Since we are fetching new content, return to the first page of results
      }),
    );

    void dispatch(fetchUserTweets(dateRange));
  },
);

export const fetchEngagementMetrics = createThunk(
  (dateRange: IAliasedDateRange) => async (dispatch, getState) => {
    const twitterUserId = getState().twitter.user.user?.id;
    const twitterUsername = getState().twitter.user.user?.screenName;

    if (!isDefined(twitterUserId) || !isDefined(twitterUsername)) {
      return;
    }

    dispatch(actions.requestSummaryStats());

    try {
      const statsData = await twitterAnalyticsApi.getEngagementMetrics({
        twitterUsername,
        twitterUserId,
        startTime: dateRange.startDate,
        endTime: dateRange.endDate,
      });

      dispatch(actions.receiveSummaryStatsSucceeded(statsData));
    } catch (e) {
      dispatch(actions.receiveSummaryStatsFailed(getRequestErrorMessage(e)));
    }
  },
);

export const fetchUserTweets = createThunk(
  (dateRange: IAliasedDateRange) => async (dispatch, getState) => {
    const twitterUserId = getState().twitter.user.user?.id;
    const twitterUsername = getState().twitter.user.user?.screenName;
    const { sortKey, limit, offset } = getState().twitter.engagement.topTweets;

    if (!isDefined(twitterUserId) || !isDefined(twitterUsername)) {
      return;
    }

    dispatch(actions.requestUserTweets());

    try {
      const tweetData = await twitterAnalyticsApi.getUserTweets({
        twitterUsername,
        twitterUserId,
        startTime: dateRange.startDate,
        endTime: dateRange.endDate,
        limit: limit,
        offset: offset,
        sortKey: sortKey,
      });

      dispatch(actions.receiveUserTweetsSucceeded(tweetData.data));
    } catch (e) {
      dispatch(actions.receiveUserTweetsFailed(getRequestErrorMessage(e)));
    }
  },
);

export const { updateTopTweetsDisplayState } = actions;

export default reducer;
